/**
 * AuthContext - Clerk Auth + Supabase Database
 * 
 * This replaces the AWS Cognito + AppSync implementation with:
 * - Clerk for authentication (OAuth, email/password)
 * - Supabase for database storage
 */

console.log('🔧 Loading AuthContext...');

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Platform } from 'react-native';

console.log('🔧 Platform:', Platform.OS);

// Import Supabase and database
let supabase, getUserByClerkId, getUserByEmail, createUser, updateUser, NotificationService;
try {
  supabase = require('./supabase').supabase;
  console.log('✅ Supabase imported');
  const database = require('./database');
  const dbExports = database.default || database;
  getUserByClerkId = database.getUserByClerkId || dbExports.getUserByClerkId;
  getUserByEmail = database.getUserByEmail || dbExports.getUserByEmail;
  createUser = database.createUser || dbExports.createUser;
  updateUser = database.updateUser || dbExports.updateUser;
  console.log('✅ Database functions imported');
  NotificationService = require('./NotificationService').default;
  console.log('✅ NotificationService imported');
} catch (error) {
  console.error('🔴 Error importing services:', error.message);
  console.error('🔴 Stack:', error.stack);
}

// Conditional Clerk imports based on platform
let useClerkAuth, useUser, useSignIn, useSignUp, useClerk, ClerkProvider, ClerkLoaded, ClerkLoading;

try {
  if (Platform.OS === 'web') {
    console.log('🔧 Loading @clerk/clerk-react for web...');
    const clerkReact = require('@clerk/clerk-react');
    useClerkAuth = clerkReact.useAuth;
    useUser = clerkReact.useUser;
    useSignIn = clerkReact.useSignIn;
    useSignUp = clerkReact.useSignUp;
    useClerk = clerkReact.useClerk;
    ClerkProvider = clerkReact.ClerkProvider;
    ClerkLoaded = clerkReact.ClerkLoaded;
    ClerkLoading = clerkReact.ClerkLoading;
    console.log('✅ @clerk/clerk-react loaded');
  } else {
    console.log('🔧 Loading @clerk/clerk-expo for native...');
    const clerkExpo = require('@clerk/clerk-expo');
    useClerkAuth = clerkExpo.useAuth;
    useUser = clerkExpo.useUser;
    useSignIn = clerkExpo.useSignIn;
    useSignUp = clerkExpo.useSignUp;
    useClerk = clerkExpo.useClerk;
    ClerkProvider = clerkExpo.ClerkProvider;
    ClerkLoaded = clerkExpo.ClerkLoaded;
    ClerkLoading = clerkExpo.ClerkLoading;
    console.log('✅ @clerk/clerk-expo loaded');
  }
} catch (error) {
  console.error('🔴 Error loading Clerk:', error.message);
  console.error('🔴 Stack:', error.stack);
}

// Ensure App never receives undefined (fixes "Element type is invalid: got undefined" on web)
if (!ClerkProvider) ClerkProvider = ({ children }) => children;
if (!ClerkLoaded) ClerkLoaded = ({ children }) => children;
if (!ClerkLoading) ClerkLoading = () => null;

// Re-export Clerk components for App.js
export { ClerkProvider, ClerkLoaded, ClerkLoading };

const CLERK_DEV_KEY = 'pk_test_cHJvbXB0LWdsb3d3b3JtLTkzLmNsZXJrLmFjY291bnRzLmRldiQ';
const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || 
  process.env.EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY ||
  CLERK_DEV_KEY;

if (CLERK_PUBLISHABLE_KEY === CLERK_DEV_KEY) {
  console.warn('⚠️ Using Clerk DEVELOPMENT key — authentication will NOT work on production domains. Set REACT_APP_CLERK_PUBLISHABLE_KEY to a pk_live_ key for production.');
}

export { CLERK_PUBLISHABLE_KEY };

const AuthContext = createContext({});

// Fallback context when Clerk fails to load (so AuthProvider never calls useClerkAuth when undefined)
const noopAsync = async () => ({ success: false, error: 'Auth not available' });
const NO_CLERK_AUTH_VALUE = {
  user: null,
  appUser: null,
  userId: null,
  clerkUserId: null,
  loading: false,
  isSignedIn: false,
  authState: {},
  signIn: noopAsync,
  signUp: noopAsync,
  verifyEmail: noopAsync,
  resendVerificationEmail: noopAsync,
  confirmSignUp: noopAsync,
  signInWithGoogle: noopAsync,
  signInWithApple: noopAsync,
  handleOAuthCallback: noopAsync,
  completeSignUpWithUsername: noopAsync,
  signOut: noopAsync,
  forgotPassword: noopAsync,
  resetPassword: noopAsync,
  createUserProfile: noopAsync,
  updateUserProfile: noopAsync,
  syncUserWithDatabase: noopAsync,
  setRegistrationInProgress: () => {},
  setNavigationRef: () => {},
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Convert snake_case database fields to camelCase for app usage
 */
export const toCamelCase = (dbUser) => {
  if (!dbUser) return null;
  return {
    id: dbUser.id,
    clerkId: dbUser.clerk_id,
    claimedBy: dbUser.claimed_by,
    username: dbUser.username,
    email: dbUser.email,
    accountType: dbUser.account_type,
    isAdmin: dbUser.is_admin,
    latitude: dbUser.latitude,
    longitude: dbUser.longitude,
    lastLocationUpdate: dbUser.last_location_update,
    profilePictureUrl: dbUser.profile_picture_url,
    pushToken: dbUser.push_token,
    city: dbUser.city,
    statusMessage: dbUser.status_message,
    isOnline: dbUser.is_online,
    lastOnlineAt: dbUser.last_online_at,
    chesscomUsername: dbUser.chesscom_username,
    chesscomBlitz: dbUser.chesscom_blitz,
    chesscomRapid: dbUser.chesscom_rapid,
    chesscomClassical: dbUser.chesscom_classical,
    chesscomLastSync: dbUser.chesscom_last_sync,
    lichessUsername: dbUser.lichess_username,
    lichessBlitz: dbUser.lichess_blitz,
    lichessRapid: dbUser.lichess_rapid,
    lichessClassical: dbUser.lichess_classical,
    lichessLastSync: dbUser.lichess_last_sync,
    rating: dbUser.rating,
    gamesPlayedInApp: dbUser.games_played_in_app,
    wins: dbUser.wins,
    losses: dbUser.losses,
    draws: dbUser.draws,
    inPersonRating: dbUser.in_person_rating,
    clubName: dbUser.club_name,
    address: dbUser.address,
    about: dbUser.about,
    website: dbUser.website,
    schedule: dbUser.schedule,
    stripeAccountId: dbUser.stripe_account_id,
    stripeOnboardingComplete: dbUser.stripe_onboarding_complete,
    tier: dbUser.tier,
    // Subscription fields
    stripeSubscriptionId: dbUser.stripe_subscription_id,
    stripeCustomerId: dbUser.stripe_customer_id,
    subscriptionStatus: dbUser.subscription_status,
    subscriptionStartedAt: dbUser.subscription_started_at,
    subscriptionEndsAt: dbUser.subscription_ends_at,
    // Social links for clubs
    instagramUrl: dbUser.instagram_url,
    twitterUrl: dbUser.twitter_url,
    facebookUrl: dbUser.facebook_url,
    // Club approval fields
    approvalStatus: dbUser.approval_status,
    submissionPitch: dbUser.submission_pitch,
    submittedAt: dbUser.submitted_at,
    reviewedAt: dbUser.reviewed_at,
    reviewedBy: dbUser.reviewed_by,
    rejectionReason: dbUser.rejection_reason,
    lastSubmissionAttempt: dbUser.last_submission_attempt,
    showDistanceInDiscover: dbUser.show_distance_in_discover,
    createdAt: dbUser.created_at,
    updatedAt: dbUser.updated_at,
  };
};

/**
 * Convert camelCase app fields to snake_case for database
 */
const toSnakeCase = (appData) => {
  const result = {};
  
  const mapping = {
    clerkId: 'clerk_id',
    claimedBy: 'claimed_by',
    accountType: 'account_type',
    isAdmin: 'is_admin',
    lastLocationUpdate: 'last_location_update',
    profilePictureUrl: 'profile_picture_url',
    pushToken: 'push_token',
    statusMessage: 'status_message',
    isOnline: 'is_online',
    lastOnlineAt: 'last_online_at',
    chesscomUsername: 'chesscom_username',
    chesscomBlitz: 'chesscom_blitz',
    // Social links for clubs
    instagramUrl: 'instagram_url',
    twitterUrl: 'twitter_url',
    facebookUrl: 'facebook_url',
    chesscomRapid: 'chesscom_rapid',
    chesscomClassical: 'chesscom_classical',
    chesscomLastSync: 'chesscom_last_sync',
    lichessUsername: 'lichess_username',
    lichessBlitz: 'lichess_blitz',
    lichessRapid: 'lichess_rapid',
    lichessClassical: 'lichess_classical',
    lichessLastSync: 'lichess_last_sync',
    gamesPlayedInApp: 'games_played_in_app',
    inPersonRating: 'in_person_rating',
    clubName: 'club_name',
    stripeAccountId: 'stripe_account_id',
    stripeOnboardingComplete: 'stripe_onboarding_complete',
    // Club approval fields
    approvalStatus: 'approval_status',
    submissionPitch: 'submission_pitch',
    submittedAt: 'submitted_at',
    reviewedAt: 'reviewed_at',
    reviewedBy: 'reviewed_by',
    rejectionReason: 'rejection_reason',
    lastSubmissionAttempt: 'last_submission_attempt',
    showDistanceInDiscover: 'show_distance_in_discover',
  };
  
  for (const [key, value] of Object.entries(appData)) {
    if (value === undefined) continue;
    const snakeKey = mapping[key] || key;
    result[snakeKey] = value;
  }
  
  return result;
};

const AuthProviderWithClerk = ({ children }) => {
  // Clerk hooks (only called when Clerk loaded)
  const { isSignedIn, isLoaded: isClerkLoaded } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const { signIn, setActive: setSignInActive } = useSignIn();
  const { signUp, setActive: setSignUpActive } = useSignUp();
  const clerk = useClerk();
  
  // Local state
  const [appUser, setAppUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authState, setAuthState] = useState({
    needsCompleteProfile: false,
    needsUsername: false,
  });
  
  // Flag to suppress needsCompleteProfile during email/password registration.
  // After email verification activates the session, syncUserWithDatabase fires before
  // createUserProfile runs.  This ref prevents CompleteProfileScreen from flashing.
  const registrationInProgressRef = useRef(false);
  
  /**
   * Sync Clerk user with Supabase database
   * Note: This does NOT create users automatically to avoid race conditions
   * with the registration flow. Users should be created through createUserProfile.
   */
  const syncUserWithDatabase = useCallback(async () => {
    if (!clerkUser) {
      setAppUser(null);
      return null;
    }
    
    try {
      console.log('🔄 Syncing user with database:', clerkUser.id);
      
      // Check if user exists in database (by clerk_id)
      let dbUser = await getUserByClerkId(clerkUser.id);

      // Fallback: if no row by clerk_id, try linking by email (existing account, different Clerk session)
      if (!dbUser) {
        const email = clerkUser.emailAddresses?.[0]?.emailAddress?.trim?.() || clerkUser.primaryEmailAddress?.emailAddress?.trim?.() || '';
        if (email) {
          try {
            const byEmail = await getUserByEmail(email);
            if (byEmail) {
              await updateUser(byEmail.id, { clerk_id: clerkUser.id });
              dbUser = await getUserByClerkId(clerkUser.id);
              if (dbUser) {
                console.log('✅ User linked by email:', dbUser.username);
              }
            }
          } catch (linkErr) {
            console.warn('⚠️ Email link fallback failed:', linkErr?.message);
          }
        }
      }

      // Restore profile from incomplete OAuth sign-up (username was not written to DB yet)
      if (!dbUser && Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
        const pendingRaw = sessionStorage.getItem('chessmaps_pending_profile');
        if (pendingRaw) {
          try {
            const parsed = JSON.parse(pendingRaw);
            const email = clerkUser.emailAddresses?.[0]?.emailAddress?.trim?.() || clerkUser.primaryEmailAddress?.emailAddress?.trim?.() || '';
            const pendingEmail = (parsed.email || '').toLowerCase();
            if (pendingEmail && pendingEmail === email.toLowerCase()) {
              const result = await createUserProfile(parsed, { clerkUserId: clerkUser.id, clerkEmail: email });
              if (result?.success && result?.user) {
                sessionStorage.removeItem('chessmaps_pending_profile');
                const camelUser = result.user;
                setAppUser(camelUser);
                setAuthState(prev => ({ ...prev, needsCompleteProfile: false, needsUsername: false }));
                return camelUser;
              }
            }
          } catch (e) {
            console.warn('Pending profile restore failed', e);
          }
        }
      }
      
      if (dbUser) {
        // Safety net: if profile picture was saved at sign-up but DB didn't get it, re-apply from sessionStorage
        if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
          try {
            const pendingRaw = sessionStorage.getItem('chessmaps_pending_profile_pic');
            if (pendingRaw) {
              const pending = JSON.parse(pendingRaw);
              const userIdMatch = pending?.userId === dbUser.id;
              const needsApply = !dbUser.profile_picture_url && userIdMatch && pending?.path;
              if (needsApply) {
                await updateUser(dbUser.id, { profile_picture_url: pending.path });
                dbUser = await getUserByClerkId(clerkUser.id);
                console.log('✅ Re-applied pending profile picture from sign-up');
              }
              sessionStorage.removeItem('chessmaps_pending_profile_pic');
            }
          } catch (e) {
            console.warn('Pending profile pic recovery failed', e);
            try { sessionStorage.removeItem('chessmaps_pending_profile_pic'); } catch (_) {}
          }
        }

        console.log('✅ User found in database:', dbUser.username, 'accountType:', dbUser.account_type);
        const camelUser = toCamelCase(dbUser);
        setAppUser(camelUser);
        
        // Check if profile needs completion.
        // Club accounts that have submitted for approval are fully set up —
        // they already provided username, profile picture, etc. in the registration form.
        const needsSetup = (!dbUser.username || dbUser.username.startsWith('user_'))
          && dbUser.account_type !== 'CLUB';
        if (needsSetup) {
          setAuthState(prev => ({ ...prev, needsCompleteProfile: true, needsUsername: true }));
        } else {
          setAuthState(prev => ({ ...prev, needsCompleteProfile: false, needsUsername: false }));
        }
        
        return camelUser;
      }
      
      // User doesn't exist - mark as needing profile completion
      // DO NOT create user here to avoid race condition with registration flow
      // which needs to set the correct account_type (PLAYER vs CLUB)
      //
      // If email/password registration is in progress, the RegisterScreen will
      // handle profile creation — skip setting needsCompleteProfile to prevent
      // CompleteProfileScreen from flashing.
      if (registrationInProgressRef.current) {
        console.log('📝 Registration in progress — deferring profile completion flag');
        return null;
      }
      const oauthEmail = clerkUser?.emailAddresses?.[0]?.emailAddress || '';
      console.log('📝 User not found in database, needs profile creation');
      setAppUser(null);
      setAuthState(prev => ({ ...prev, needsCompleteProfile: true, needsUsername: true, oauthPendingEmail: oauthEmail || prev?.oauthPendingEmail }));
      
      return null;
    } catch (error) {
      console.error('❌ Error syncing user with database:', error);
      setAppUser(null);
      return null;
    }
  }, [clerkUser]);
  
  /**
   * Watch for Clerk auth state changes
   */
  useEffect(() => {
    if (!isClerkLoaded) {
      return;
    }
    
    if (isSignedIn && clerkUser) {
      syncUserWithDatabase().finally(() => setLoading(false));
    } else {
      setAppUser(null);
      
      // Check if there's an active sign-up in progress (OAuth sign-up waiting for username)
      // If signUp exists and is in 'missing_requirements' status, we're in OAuth sign-up flow
      const hasActiveSignUp = signUp && (signUp.status === 'missing_requirements' || signUp.status === 'needs_username');
      
      // Only clear needsCompleteProfile if we're not in the middle of OAuth sign-up
      // When needsUsername is true OR there's an active sign-up, we're in the middle of OAuth sign-up
      // and need to preserve the state so CompleteProfileScreen can complete the sign-up
      // Use functional update to get the latest state value
      setAuthState(prev => {
        if (prev?.needsUsername || hasActiveSignUp) {
          // Preserve the state - ensure needsCompleteProfile is true if needsUsername is true
          if (prev?.needsUsername && !prev?.needsCompleteProfile) {
            return { ...prev, needsCompleteProfile: true };
          }
          return prev;
        } else {
          return { needsCompleteProfile: false, needsUsername: false };
        }
      });
      setLoading(false);
    }
  }, [isClerkLoaded, isSignedIn, clerkUser, syncUserWithDatabase, signUp?.status]);
  
  /**
   * Update user profile in database
   * @param {object} profileData - camelCase fields to update
   * @param {{ userId?: string }} [options] - pass userId when appUser state is stale (e.g. right after createUserProfile in the same tick)
   */
  const updateUserProfile = async (profileData, options = {}) => {
    const explicitUserId = options.userId;

    if (explicitUserId) {
      try {
        console.log('🔄 Updating user profile (explicit userId):', explicitUserId);
        const snakeData = toSnakeCase(profileData);
        const updatedDbUser = await updateUser(explicitUserId, snakeData);
        const camelUser = toCamelCase(updatedDbUser);
        setAppUser(camelUser);
        if (profileData.username) {
          setAuthState(prev => ({ ...prev, needsCompleteProfile: false, needsUsername: false }));
        }
        console.log('✅ User profile updated (explicit userId)');
        return { success: true, user: camelUser };
      } catch (error) {
        console.error('❌ Error updating user profile (explicit userId):', error);
        return { success: false, error: error.message };
      }
    }

    // If appUser doesn't exist yet but clerkUser does, sync first or create profile
    if (!appUser?.id) {
      if (clerkUser?.id) {
        // Use sync return value (not appUser) — React state updates are async
        const synced = await syncUserWithDatabase();

        if (synced?.id) {
          try {
            console.log('🔄 Updating user profile (post-sync):', synced.id);
            const snakeData = toSnakeCase(profileData);
            const updatedDbUser = await updateUser(synced.id, snakeData);
            const camelUser = toCamelCase(updatedDbUser);
            setAppUser(camelUser);
            if (profileData.username) {
              setAuthState(prev => ({ ...prev, needsCompleteProfile: false, needsUsername: false }));
            }
            console.log('✅ User profile updated');
            return { success: true, user: camelUser };
          } catch (error) {
            console.error('❌ Error updating user profile:', error);
            return { success: false, error: error.message };
          }
        }

        return await createUserProfile(profileData);
      }

      return {
        success: false,
        error: 'Your session is still loading. Please wait a moment and try again.',
      };
    }
    
    try {
      console.log('🔄 Updating user profile:', appUser.id);
      const snakeData = toSnakeCase(profileData);
      const updatedDbUser = await updateUser(appUser.id, snakeData);
      const camelUser = toCamelCase(updatedDbUser);
      setAppUser(camelUser);
      
      // Clear needsCompleteProfile if username was set
      if (profileData.username) {
        setAuthState(prev => ({ ...prev, needsCompleteProfile: false, needsUsername: false }));
      }
      
      console.log('✅ User profile updated');
      return { success: true, user: camelUser };
    } catch (error) {
      console.error('❌ Error updating user profile:', error);
      return { success: false, error: error.message };
    }
  };
  
  /**
   * Set registration-in-progress flag (used by RegisterScreen to prevent race conditions)
   */
  const setRegistrationInProgress = useCallback((value) => {
    registrationInProgressRef.current = !!value;
  }, []);
  
  /**
   * Sign in with email and password
   */
  const signInWithEmail = async (email, password) => {
    try {
      console.log('🔄 Signing in with email:', email);
      const result = await signIn.create({
        identifier: email,
        password,
      });
      
      if (result.status === 'complete') {
        await setSignInActive({ session: result.createdSessionId });
        console.log('✅ Sign in successful');
        return { success: true };
      }
      
      return { success: false, error: 'Sign in incomplete' };
    } catch (error) {
      console.error('❌ Sign in error:', error);
      return { success: false, error: error.errors?.[0]?.message || error.message };
    }
  };
  
  /**
   * Sign up with email and password
   */
  const signUpWithEmail = async (email, password, username) => {
    try {
      // Email should already be trimmed from RegisterScreen, but trim again for safety
      const trimmedEmail = (email || '').trim();
      
      console.log('🔄 Signing up with email:', {
        original: email,
        trimmed: trimmedEmail,
        length: trimmedEmail.length,
        hasAt: trimmedEmail.includes('@')
      });
      
      // Only check if email is completely empty - let Clerk handle format validation
      if (!trimmedEmail) {
        console.error('❌ Email is empty after trimming');
        return {
          success: false,
          error: 'Please enter your email address.',
          errorCode: 'empty_email'
        };
      }
      
      // Check for common email format issues that might cause problems
      // Check for non-ASCII characters that might cause encoding issues
      const emailBytes = new TextEncoder().encode(trimmedEmail);
      const emailString = new TextDecoder().decode(emailBytes);
      if (emailString !== trimmedEmail) {
        console.warn('⚠️ Email encoding mismatch detected');
      }
      
      // Log email character analysis
      const emailChars = Array.from(trimmedEmail);
      const hasUnusualChars = emailChars.some(char => {
        const code = char.charCodeAt(0);
        // Check for non-printable or unusual characters
        return (code < 32 && code !== 9 && code !== 10 && code !== 13) || 
               (code > 126 && code < 160) ||
               code > 0x10FFFF;
      });
      
      if (hasUnusualChars) {
        console.warn('⚠️ Email contains unusual characters:', {
          email: trimmedEmail,
          chars: emailChars.map(c => ({ char: c, code: c.charCodeAt(0) }))
        });
      }
      
      // Log exact values being sent to Clerk
      console.log('📤 Sending to Clerk:', {
        emailAddress: trimmedEmail,
        emailAddressLength: trimmedEmail.length,
        emailAddressBytes: new TextEncoder().encode(trimmedEmail).length,
        emailAddressHex: Array.from(new TextEncoder().encode(trimmedEmail)).map(b => b.toString(16).padStart(2, '0')).join(' '),
        username: username,
        usernameLength: username?.length,
        passwordLength: password?.length
      });
      
      const result = await signUp.create({
        emailAddress: trimmedEmail,
        password,
        username,
      });
      
      if (result.status === 'complete') {
        await setSignUpActive({ session: result.createdSessionId });
        console.log('✅ Sign up successful');
        return { success: true };
      }
      
      // May need email verification
      if (result.status === 'missing_requirements') {
        console.log('📧 Email verification required');
        return { success: true, needsVerification: true, signUp: result };
      }
      
      return { success: false, error: 'Sign up incomplete' };
    } catch (error) {
      console.error('❌ Sign up error:', error);
      console.error('❌ Full error object:', JSON.stringify(error, null, 2));
      
      // Check for specific Clerk error codes
      const firstError = error.errors?.[0];
      const errorCode = firstError?.code;
      const longMessage = firstError?.longMessage || '';
      const shortMessage = firstError?.message || '';
      const fullMessage = longMessage || shortMessage || error.message || '';
      
      // Check ALL errors, not just the first one
      const allErrors = error.errors || [];
      
      console.log('🔍 All error details:', {
        totalErrors: allErrors.length,
        errors: allErrors.map(err => ({
          code: err.code,
          message: err.message,
          longMessage: err.longMessage,
          param: err.meta?.paramName || err.meta?.param_name
        }))
      });
      
      // Collect all error messages
      const errorMessages = [];
      const errorCodes = [];
      
      for (const err of allErrors) {
        const errCode = err.code;
        const errParam = err.meta?.paramName || err.meta?.param_name || '';
        const errMessage = err.longMessage || err.message || '';
        
        errorCodes.push(errCode);
        
        // Email already exists
        if (errCode === 'form_identifier_exists') {
          errorMessages.push('An account with this email already exists. Please sign in instead or use a different email address.');
        }
        // Username already exists
        else if (errCode === 'form_username_exists') {
          errorMessages.push('This username is already taken. Please choose another.');
        }
        // Email format validation
        else if (errCode === 'form_param_format_invalid' && 
                 (errParam.toLowerCase().includes('email') || errMessage.toLowerCase().includes('email'))) {
          errorMessages.push(errMessage || 'Please enter a valid email address.');
        }
        // Username format validation
        else if (errCode === 'form_username_invalid_character') {
          errorMessages.push(errMessage || 'Username can only contain letters, numbers, hyphens (-), and underscores (_).');
        }
        // Password errors
        else if (errCode === 'form_password_length_too_short') {
          errorMessages.push('Password must be at least 8 characters long.');
        }
        else if (errCode === 'form_password_pwned' || errCode === 'form_password_too_common') {
          errorMessages.push('This password is too common. Please choose a stronger password.');
        }
        // Other format errors
        else if (errCode === 'form_param_format_invalid') {
          errorMessages.push(errMessage || `Invalid format for ${errParam}.`);
        }
        // Generic error
        else {
          errorMessages.push(errMessage || 'Validation error.');
        }
      }
      
      // If we have multiple errors, combine them
      if (errorMessages.length > 0) {
        const combinedMessage = errorMessages.length === 1 
          ? errorMessages[0] 
          : errorMessages.join(' ');
        
        return {
          success: false,
          error: combinedMessage,
          errorCode: errorCodes[0],
          allErrors: allErrors
        };
      }
      
      // Fallback: Use the full error message from Clerk if we didn't catch it above
      const errorMessage = fullMessage || 'Registration failed. Please try again.';
      
      return { success: false, error: errorMessage, errorCode };
    }
  };
  
  /**
   * Resend verification email
   */
  const resendVerificationEmail = async () => {
    try {
      const result = await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      
      console.log('📧 Verification email resent');
      return { success: true };
    } catch (error) {
      console.error('❌ Resend verification email error:', error);
      const firstError = error.errors?.[0];
      const errorMessage = firstError?.longMessage || firstError?.message || error.message || 'Failed to resend verification email';
      return { success: false, error: errorMessage };
    }
  };
  
  /**
   * Verify email with code
   */
  const verifyEmail = async (code) => {
    try {
      const result = await signUp.attemptEmailAddressVerification({ code });
      
      if (result.status === 'complete') {
        await setSignUpActive({ session: result.createdSessionId });
        console.log('✅ Email verified successfully');
        return { success: true };
      }
      
      return { success: false, error: 'Verification incomplete' };
    } catch (error) {
      console.error('❌ Verification error:', error);
      const firstError = error.errors?.[0];
      const errorMessage = firstError?.longMessage || firstError?.message || error.message || 'Verification failed';
      
      // Provide better error messages for common cases
      if (firstError?.code === 'form_code_incorrect') {
        return { success: false, error: 'Invalid verification code. Please check and try again.' };
      }
      if (firstError?.code === 'form_code_expired') {
        return { success: false, error: 'Verification code has expired. Please request a new one.' };
      }
      
      return { success: false, error: errorMessage };
    }
  };
  
  /**
   * Confirm sign up (alias for verifyEmail for compatibility)
   */
  const confirmSignUp = async (username, code) => {
    // Note: username parameter is not needed for Clerk, but kept for compatibility
    return verifyEmail(code);
  };
  
  /**
   * Create user profile in database
   * This should be called after email verification and sign-in
   * @param {Object} profileData - Profile data
   * @param {Object} [options] - Optional overrides for OAuth flow when clerkUser context may not have updated yet
   * @param {string} [options.clerkUserId] - Clerk user ID (e.g. from completeSignUpWithUsername.createdUserId)
   * @param {string} [options.clerkEmail] - Email (e.g. from authState.oauthPendingEmail)
   */
  const createUserProfile = async (profileData, options = {}) => {
    try {
      // Prefer explicit clerkUserId from options, then current Clerk user from hooks,
      // then the low-level clerk.user (which can sometimes be available slightly earlier).
      let clerkId = options.clerkUserId || clerkUser?.id || clerk?.user?.id;
      const clerkEmail = options.clerkEmail || clerkUser?.emailAddresses?.[0]?.emailAddress;

      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Debug-Session-Id': '81b6b1',
        },
        body: JSON.stringify({
          sessionId: '81b6b1',
          runId: 'pre-fix-1',
          hypothesisId: 'H1',
          location: 'AuthContext.js:createUserProfile:initial',
          message: 'Initial clerk identifiers for createUserProfile',
          data: {
            hasOptionsClerkUserId: !!options.clerkUserId,
            hasHookClerkUser: !!clerkUser?.id,
            hasLowLevelClerkUser: !!clerk?.user?.id,
          },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion agent log
      
      // For OAuth flows, Clerk context can lag or even be unavailable in some
      // environments (e.g. when using the web Google fallback). To avoid
      // blocking profile creation on the first run, fall back to a synthetic
      // placeholder clerk_id derived from email when no real Clerk ID exists.
      let usedPlaceholderClerkId = false;
      if (!clerkId) {
        usedPlaceholderClerkId = true;
        // Don't create a DB row yet — no username is reserved until sign-up completes.
        // Caller will store pendingProfileData and redirect to login; when user signs in
        // again with a real Clerk session, syncUserWithDatabase will create the profile.
        return {
          success: true,
          usedPlaceholderClerkId: true,
          pendingProfileData: { ...profileData, email: clerkEmail || profileData.email },
        };
      }
      
      // Get or create the user record
      let dbUser = await getUserByClerkId(clerkId);
      
      if (!dbUser) {
        // User doesn't exist yet, create them
        console.log('📝 Creating new user profile in database');
        const email = clerkEmail || profileData.email;
        const username = profileData.username || `user_${clerkId.slice(-8)}`;
        
        const userData = {
          clerk_id: clerkId,
          username: username.toLowerCase(),
          email: email?.toLowerCase(),
          account_type: profileData.accountType || 'PLAYER',
          rating: profileData.rating || 1200,
          games_played_in_app: profileData.gamesPlayedInApp || 0,
          wins: 0,
          losses: 0,
          draws: 0,
          in_person_rating: profileData.rating || 1200,
        };
        
        // Add club-specific fields if it's a club account
        if (profileData.accountType === 'CLUB') {
          if (profileData.clubName) userData.club_name = profileData.clubName;
          if (profileData.address) userData.address = profileData.address;
          if (profileData.submissionPitch) userData.submission_pitch = profileData.submissionPitch;
          if (profileData.website) userData.website = profileData.website;
          if (profileData.instagramUrl) userData.instagram_url = profileData.instagramUrl;
          if (profileData.twitterUrl) userData.twitter_url = profileData.twitterUrl;
          if (profileData.facebookUrl) userData.facebook_url = profileData.facebookUrl;
          userData.approval_status = profileData.approvalStatus || 'PENDING';
          if (profileData.submittedAt) userData.submitted_at = profileData.submittedAt;
          if (profileData.lastSubmissionAttempt) userData.last_submission_attempt = profileData.lastSubmissionAttempt;
        } else {
          // Player-specific fields
          if (profileData.chesscomUsername) userData.chesscom_username = profileData.chesscomUsername;
          if (profileData.lichessUsername) userData.lichess_username = profileData.lichessUsername;
        }
        if (profileData.profilePictureUrl) userData.profile_picture_url = profileData.profilePictureUrl;
        if (profileData.city) userData.city = profileData.city;
        if (profileData.bio) userData.bio = profileData.bio;
        if (profileData.tiktokUrl) userData.tiktok_url = profileData.tiktokUrl;
        
        dbUser = await createUser(userData);
        console.log('✅ User profile created:', dbUser.username);
        
        // Auto-claim: If this is a new club account, set claimed_by to self
        if (profileData.accountType === 'CLUB' && dbUser.id) {
          console.log('🏛️ Auto-claiming club for creator:', dbUser.id);
          dbUser = await updateUser(dbUser.id, { claimed_by: dbUser.id });
          console.log('✅ Club auto-claimed successfully');
        }
      } else {
        // User exists, update their profile
        console.log('🔄 Updating existing user profile');
        console.log('   Current account_type:', dbUser.account_type);
        console.log('   New account_type:', profileData.accountType);
        
        const snakeData = toSnakeCase(profileData);
        
        // Ensure username is lowercase
        if (snakeData.username) {
          snakeData.username = snakeData.username.toLowerCase();
        }
        
        // During registration, always update account_type if provided
        // This handles cases where user was created with wrong type
        if (profileData.accountType && snakeData.account_type !== dbUser.account_type) {
          console.log('   ⚠️ Account type mismatch detected, updating from', dbUser.account_type, 'to', snakeData.account_type);
        }
        
        // Ensure account_type is included in update
        if (profileData.accountType) {
          snakeData.account_type = profileData.accountType;
        }
        
        console.log('   Updating with data:', Object.keys(snakeData));
        dbUser = await updateUser(dbUser.id, snakeData);
        console.log('✅ User profile updated, new account_type:', dbUser.account_type);
      }
      
      // Update app state
      const camelUser = toCamelCase(dbUser);
      setAppUser(camelUser);
      
      // Clear needsCompleteProfile if username was set
      if (profileData.username) {
        setAuthState(prev => ({ ...prev, needsCompleteProfile: false, needsUsername: false }));
      }
      
      return { success: true, user: camelUser, usedPlaceholderClerkId: !!usedPlaceholderClerkId };
    } catch (error) {
      console.error('❌ Error creating user profile:', error);
      
      // Check for duplicate username/email errors
      if (error.message?.includes('duplicate') || error.message?.includes('unique') || error.code === '23505') {
        if (error.message?.includes('username')) {
          return { success: false, error: 'This username is already taken. Please choose another.' };
        }
        if (error.message?.includes('email')) {
          return { success: false, error: 'An account with this email already exists.' };
        }
        return { success: false, error: 'This information is already in use. Please try different values.' };
      }
      
      return { success: false, error: error.message || 'Failed to create profile. Please try again.' };
    }
  };
  
  /**
   * Sign in with Google
   */
  const signInWithGoogle = async () => {
    try {
      console.log('🔄 Starting Google Sign-In...');
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '81b6b1' },
        body: JSON.stringify({
          sessionId: '81b6b1',
          runId: 'reroute-investigation-1',
          hypothesisId: 'H1',
          location: 'AuthContext.js:signInWithGoogle:clerk',
          message: 'Using Clerk Google sign-in path',
          data: { platform: Platform.OS },
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion agent log
      
      // Get the redirect URL based on platform
      const redirectUrl = Platform.OS === 'web'
        ? `${window.location.origin}/sso-callback`
        : 'chessmaps://sso-callback';
      
      // Start OAuth flow
      const result = await signIn.create({
        strategy: 'oauth_google',
        redirectUrl,
      });
      
      // Get the authorization URL
      const { externalVerificationRedirectURL } = result.firstFactorVerification;
      
      if (Platform.OS === 'web') {
        // Store the signIn ID for retrieval after redirect
        localStorage.setItem('clerk_sign_in_id', result.id);
        // Redirect to Google
        window.location.href = externalVerificationRedirectURL.toString();
        return { success: true, redirecting: true };
      } else {
        // For native, use expo-web-browser
        const WebBrowser = require('expo-web-browser');
        const authResult = await WebBrowser.openAuthSessionAsync(
          externalVerificationRedirectURL.toString(),
          redirectUrl
        );
        
        if (authResult.type === 'success') {
          // Reload the sign-in to get the session
          await signIn.reload();
          if (signIn.status === 'complete') {
            await setSignInActive({ session: signIn.createdSessionId });
            return { success: true };
          }
        }
        
        return { success: false, error: 'OAuth flow cancelled' };
      }
    } catch (error) {
      console.error('❌ Google Sign-In error:', error);
      return { success: false, error: error.errors?.[0]?.message || error.message };
    }
  };
  
  /**
   * Sign in with Apple
   */
  const signInWithApple = async () => {
    try {
      console.log('🔄 Starting Apple Sign-In...');
      
      const redirectUrl = Platform.OS === 'web'
        ? `${window.location.origin}/sso-callback`
        : 'chessmaps://sso-callback';
      
      const result = await signIn.create({
        strategy: 'oauth_apple',
        redirectUrl,
      });
      
      const { externalVerificationRedirectURL } = result.firstFactorVerification;
      
      if (Platform.OS === 'web') {
        localStorage.setItem('clerk_sign_in_id', result.id);
        window.location.href = externalVerificationRedirectURL.toString();
        return { success: true, redirecting: true };
      } else {
        const WebBrowser = require('expo-web-browser');
        const authResult = await WebBrowser.openAuthSessionAsync(
          externalVerificationRedirectURL.toString(),
          redirectUrl
        );
        
        if (authResult.type === 'success') {
          await signIn.reload();
          if (signIn.status === 'complete') {
            await setSignInActive({ session: signIn.createdSessionId });
            return { success: true };
          }
        }
        
        return { success: false, error: 'OAuth flow cancelled' };
      }
    } catch (error) {
      console.error('❌ Apple Sign-In error:', error);
      return { success: false, error: error.errors?.[0]?.message || error.message };
    }
  };
  
  /**
   * Complete OAuth callback (for web)
   * Clerk automatically processes the OAuth callback when the page loads.
   * We just need to reload the signIn state and check its status.
   */
  const handleOAuthCallback = async () => {
    try {
      console.log('🔄 Handling OAuth callback...');
      
      // Reload the signIn object to get updated status after OAuth redirect
      await signIn.reload();
      
      console.log('🔄 Sign-in status after reload:', signIn.status);
      
      // Check if sign-in is complete
      if (signIn.status === 'complete') {
        await setSignInActive({ session: signIn.createdSessionId });
        console.log('✅ OAuth sign-in completed');
        return { success: true };
      }
      
      // Check if this is a new user (needs sign-up transfer)
      if (signIn.firstFactorVerification?.status === 'transferable') {
        console.log('📝 New user from OAuth, transferring to sign-up');
        const signUpResult = await signUp.create({ transfer: true });
        
        if (signUpResult.status === 'complete') {
          await setSignUpActive({ session: signUpResult.createdSessionId });
          console.log('✅ OAuth sign-up completed');
          return { success: true, isNewUser: true };
        }
        
        // May need username
        if (signUpResult.status === 'missing_requirements') {
          console.log('⚠️ Sign-up needs additional requirements (likely username)');
          
          // Try to reload the sign-up to see current status
          await signUp.reload();
          
          // If sign-up is now complete after reload, activate it
          if (signUp.status === 'complete') {
            await setSignUpActive({ session: signUp.createdSessionId });
            return { success: true };
          }
          
          // If we have a pending profile from the timeout redirect, complete sign-up with that username
          // so the user gets a session and syncUserWithDatabase can create the DB user (no second sign-up flow).
          let oauthEmail = '';
          try {
            const e = signUp.emailAddress || signUp.verifiedEmailAddresses?.[0] || signUp.unverifiedFields?.email_address;
            oauthEmail = (typeof e === 'string' ? e : e?.emailAddress) || '';
          } catch (_) {}
          if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined' && oauthEmail) {
            const pendingRaw = sessionStorage.getItem('chessmaps_pending_profile');
            if (pendingRaw) {
              try {
                const parsed = JSON.parse(pendingRaw);
                const pendingEmail = (parsed.email || '').toLowerCase();
                if (pendingEmail && pendingEmail === oauthEmail.trim().toLowerCase() && parsed.username) {
                  console.log('📝 Completing sign-up with pending profile username:', parsed.username);
                  await signUp.update({ username: parsed.username });
                  await signUp.reload();
                  if (signUp.status === 'complete') {
                    await setSignUpActive({ session: signUp.createdSessionId });
                    console.log('✅ Sign-up completed from pending profile');
                    return { success: true };
                  }
                }
              } catch (err) {
                console.warn('Pending profile sign-up completion failed', err);
              }
            }
          }
          
          // Still needs username - set auth state so navigation can show CompleteProfile
          setAuthState(prev => ({ 
            ...prev, 
            needsCompleteProfile: true, 
            needsUsername: true,
            oauthPendingEmail: oauthEmail || prev?.oauthPendingEmail 
          }));
          
          return { success: false, needsUsername: true, signUp: signUpResult };
        }
        
        return { success: false, error: 'Sign-up incomplete after transfer' };
      }
      
      // If we get here, sign-in is not complete and not transferable
      console.warn('⚠️ Sign-in status:', signIn.status);
      return { success: false, error: `OAuth callback incomplete. Status: ${signIn.status}` };
    } catch (error) {
      console.error('❌ OAuth callback error:', error);
      const firstError = error.errors?.[0];
      const errorMessage = firstError?.longMessage || firstError?.message || error.message || 'OAuth callback failed';
      return { success: false, error: errorMessage };
    }
  };
  
  /**
   * Complete sign-up with username (for OAuth users)
   * Returns createdUserId when successful so caller can create profile before clerkUser context updates.
   *
   * Strategy: try signUp.update() first. If Clerk returns 405 (sign-up object is stale /
   * already consumed), fall back to updating the username on the already-authenticated user.
   */
  const completeSignUpWithUsername = async (username) => {
    // ── Helper: check if a Clerk error is "reverification required" or 405 ──
    const isReverifOrStale = (err) => {
      const code = err?.status || err?.errors?.[0]?.code;
      const msg = err?.message || err?.errors?.[0]?.message || '';
      return code === 405 || code === 403
        || /reverification|re-?verif|stale|method not allowed/i.test(msg)
        || msg.includes('405');
    };

    // ── Helper: treat "username already taken" from Clerk as non-fatal ──
    const isUsernameTakenError = (err) => {
      const code = err?.errors?.[0]?.code;
      const msg = err?.errors?.[0]?.message || err?.message || '';
      return code === 'form_username_exists' ||
        /username.*taken/i.test(msg) ||
        /already exists/i.test(msg);
    };

    // ── Path A: user is already signed in (session created during OAuth) ──
    if (clerkUser) {
      // If Clerk already has a username for this user, no update needed at all
      if (clerkUser.username) {
        console.log('✅ clerkUser already has username:', clerkUser.username, '— skipping Clerk update');
        return {
          success: true,
          createdUserId: clerkUser.id,
          createdSessionId: clerk?.session?.id || null,
        };
      }

      // Try setting username on the user object
      try {
        console.log('📝 User already signed in — updating username on user object');
        await clerkUser.update({ username });
        return {
          success: true,
          createdUserId: clerkUser.id,
          createdSessionId: clerk?.session?.id || null,
        };
      } catch (err) {
        console.error('❌ clerkUser.update error:', err);
        // If it's a "reverification required" or 403/405, just proceed —
        // the username will be stored in Supabase; Clerk username is optional.
        if (isReverifOrStale(err)) {
          console.log('⚠️ Reverification required — proceeding without Clerk username update');
          return {
            success: true,
            createdUserId: clerkUser.id,
            createdSessionId: clerk?.session?.id || null,
          };
        }
        // If Clerk just says this username is taken in its own namespace,
        // continue — we only enforce uniqueness in Supabase, not in Clerk.
        if (isUsernameTakenError(err)) {
          console.log('⚠️ Clerk username already exists — proceeding without Clerk username update');
          return {
            success: true,
            createdUserId: clerkUser.id,
            createdSessionId: clerk?.session?.id || null,
          };
        }
        // Other genuine errors should still surface
        return { success: false, error: err.errors?.[0]?.message || err.message };
      }
    }

    // ── Path B: no active user yet — finish the sign-up flow ──
    try {
      await signUp.update({ username });

      if (signUp.status === 'complete') {
        await setSignUpActive({ session: signUp.createdSessionId });
        return {
          success: true,
          createdUserId: signUp.createdUserId,
          createdSessionId: signUp.createdSessionId,
        };
      }

      return { success: false, error: 'Sign-up incomplete after username' };
    } catch (error) {
      console.error('❌ signUp.update error:', error);

      // 405 / reverification = sign-up object is stale. The session may already exist —
      // reload Clerk and try to proceed with the existing user.
      if (isReverifOrStale(error)) {
        try {
          console.log('🔄 signUp.update failed (stale) — checking for existing session');
          await clerk.session?.reload?.();
          let freshUser = clerk.user;
          if (!freshUser) {
            await new Promise(r => setTimeout(r, 800));
            freshUser = clerk.user;
          }
          if (freshUser) {
            // User exists — try to set username, but don't block on reverification
            if (!freshUser.username) {
              try {
                await freshUser.update({ username });
              } catch (updateErr) {
                if (!isReverifOrStale(updateErr)) throw updateErr;
                console.log('⚠️ Reverification on fallback — proceeding without Clerk username');
              }
            }
            return {
              success: true,
              createdUserId: freshUser.id,
              createdSessionId: clerk.session?.id || null,
            };
          }
        } catch (fallbackErr) {
          console.error('❌ Fallback error:', fallbackErr);
          return { success: false, error: fallbackErr.errors?.[0]?.message || fallbackErr.message };
        }
      }

      // If Clerk rejects because the username is taken, retry with a unique
      // random username so the sign-up actually completes. We only care about
      // the Supabase username; Clerk's is cosmetic.
      if (isUsernameTakenError(error)) {
        try {
          console.log('⚠️ Clerk username taken — retrying with unique fallback username');
          const fallback = `${username}_${Math.random().toString(36).slice(2, 7)}`;
          await signUp.update({ username: fallback });
          if (signUp.status === 'complete') {
            await setSignUpActive({ session: signUp.createdSessionId });
            return {
              success: true,
              createdUserId: signUp.createdUserId,
              createdSessionId: signUp.createdSessionId,
            };
          }
          // Still not complete — try reload as last resort
          await signUp.reload();
          if (signUp.status === 'complete') {
            await setSignUpActive({ session: signUp.createdSessionId });
            return {
              success: true,
              createdUserId: signUp.createdUserId,
              createdSessionId: signUp.createdSessionId,
            };
          }
        } catch (fallbackErr) {
          console.error('❌ Fallback after username-exists error failed:', fallbackErr);
          return { success: false, error: fallbackErr.errors?.[0]?.message || fallbackErr.message };
        }
      }

      return { success: false, error: error.errors?.[0]?.message || error.message };
    }
  };
  
  /**
   * Sign out
   */
  const signOut = async () => {
    try {
      console.log('🔄 Signing out...');
      await clerk.signOut();
      setAppUser(null);
      setAuthState({ needsCompleteProfile: false, needsUsername: false });
      
      // Force reload on web to clear all cached data
      if (Platform.OS === 'web') {
        window.location.href = '/';
      }
      
      console.log('✅ Sign out successful');
      return { success: true };
    } catch (error) {
      console.error('❌ Sign out error:', error);
      return { success: false, error: error.message };
    }
  };
  
  /**
   * Forgot password - send reset code
   */
  const forgotPassword = async (email) => {
    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.errors?.[0]?.message || error.message };
    }
  };
  
  /**
   * Reset password with code
   */
  const resetPassword = async (code, newPassword) => {
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password: newPassword,
      });
      
      if (result.status === 'complete') {
        await setSignInActive({ session: result.createdSessionId });
        return { success: true };
      }
      
      return { success: false, error: 'Password reset incomplete' };
    } catch (error) {
      return { success: false, error: error.errors?.[0]?.message || error.message };
    }
  };
  
  /**
   * Register for push notifications
   */
  const registerPushNotifications = async () => {
    if (!appUser?.id) return;
    
    try {
      const pushToken = await NotificationService.registerForPushNotifications();
      if (pushToken && pushToken !== appUser.pushToken) {
        await updateUserProfile({ pushToken });
      }
    } catch (error) {
      console.warn('⚠️ Push notification registration failed:', error);
    }
  };
  
  // Register for push notifications when user is set
  useEffect(() => {
    if (appUser?.id) {
      registerPushNotifications();
    }
  }, [appUser?.id]);
  
  const value = {
    // State
    user: clerkUser,
    appUser,
    userId: appUser?.id,
    clerkUserId: clerkUser?.id,
    loading: loading || !isClerkLoaded,
    isSignedIn,
    authState,
    
    // Auth methods
    signIn: signInWithEmail,
    signUp: signUpWithEmail,
    verifyEmail,
    resendVerificationEmail,
    confirmSignUp,
    signInWithGoogle,
    signInWithApple,
    handleOAuthCallback,
    completeSignUpWithUsername,
    signOut,
    forgotPassword,
    resetPassword,
    
    // Profile methods
    createUserProfile,
    updateUserProfile,
    syncUserWithDatabase,
    setRegistrationInProgress,
    
    // Convenience
    setNavigationRef: () => {}, // Legacy compatibility
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Web-only fallback provider when Clerk fails to load.
// Uses SocialAuthService.web directly for Google sign-in so auth isn't blocked on Clerk bundling issues.
const AuthProviderWebFallback = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const SocialAuthService = require('./SocialAuthService.web').default;
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '81b6b1' },
        body: JSON.stringify({
          sessionId: '81b6b1',
          runId: 'reroute-investigation-1',
          hypothesisId: 'H2',
          location: 'AuthContext.js:signInWithGoogle:fallback',
          message: 'Using web fallback Google sign-in path',
          data: {},
          timestamp: Date.now(),
        }),
      }).catch(() => {});
      // #endregion agent log
      const result = await SocialAuthService.signInWithGoogle();
      setLoading(false);

      if (!result.success) {
        return result;
      }

      const profile = {
        id: result.id,
        email: result.email,
        name: result.name,
        picture: result.picture,
        provider: 'google',
      };
      setUser(profile);
      return { success: true, user: profile };
    } catch (error) {
      setLoading(false);
      return { success: false, error: error.message || 'Google sign-in failed' };
    }
  };

  const signOut = async () => {
    setUser(null);
    return { success: true };
  };

  const value = {
    ...NO_CLERK_AUTH_VALUE,
    user,
    appUser: null,
    userId: user?.id || null,
    clerkUserId: null,
    loading,
    isSignedIn: !!user,
    authState: {},
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider = ({ children }) => {
  if (typeof useClerkAuth !== 'function') {
    if (Platform.OS === 'web') {
      // Use web fallback that talks directly to Google instead of failing auth completely.
      return <AuthProviderWebFallback>{children}</AuthProviderWebFallback>;
    }
    return <AuthContext.Provider value={NO_CLERK_AUTH_VALUE}>{children}</AuthContext.Provider>;
  }
  return <AuthProviderWithClerk>{children}</AuthProviderWithClerk>;
};

export const setUserAdmin = async (email, isAdmin = true) => {
  // This needs to be done via Supabase now
  console.log('setUserAdmin: Use Supabase dashboard to update user admin status');
};

export const debugAuthStatus = async () => {
  console.log('debugAuthStatus: Use Clerk dashboard for auth debugging');
};

export default AuthContext;
